import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import { ArrowRight, Sparkles, BookOpen, MessageSquareQuote, Wand2 } from "lucide-react";

export default function BrandLearningPublicPage() {
  return (
    <>
      <SEO
        title="Postmoro Brand Learning | Match Your Existing Social Media Style"
        description="Use Brand Learning to help Postmoro understand your existing social media voice and generate more on-brand content."
        path="/features/brand-learning"
      />
      <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]" data-testid="brand-learning-public-page">
        <MarketingHeader activePath="/features" />

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> Brand Learning
            </div>
            <h1 className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]" data-testid="brand-learning-public-hero-title">
              Postmoro Brand Learning — match your existing style.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              Paste examples of the posts your business already writes and
              Postmoro adapts new drafts to match that voice. Ideal for
              businesses with an established social presence that want the AI
              to sound like them, not a generic template.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/login"><Button size="lg" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold">Start free <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              <Link to="/features"><Button size="lg" variant="outline" className="border-[color:var(--border-subtle)] text-[color:var(--text-1)]">All features</Button></Link>
            </div>
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5" data-testid="brand-learning-step-1">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]"><MessageSquareQuote className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-base font-semibold">1 · Add example posts</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">Paste in captions you're proud of — the ones that already sound like your business. A handful is enough to get started.</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5" data-testid="brand-learning-step-2">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]"><BookOpen className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-base font-semibold">2 · Postmoro learns your style</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">Postmoro extracts the patterns from your examples — sentence rhythm, tone, common phrases — and turns them into guidance for future drafts.</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5" data-testid="brand-learning-step-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]"><Wand2 className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-base font-semibold">3 · Better-matching drafts</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">Manual generations and Autopilot both pick up the learned style so new posts feel closer to how your business already writes.</p>
            </div>
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 text-sm text-[color:var(--text-2)] leading-relaxed">
            Brand Learning is a guidance layer — Postmoro uses it to steer draft
            generation. It does not train a private AI model on your behalf and
            it does not replace your review of each draft before it publishes.
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
